import { executeMutation, getRequestScope, queryClient, subscribeRequestScope } from './client';

let sequence = 0;

export function managedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response>;
export function managedFetch<T>(input: RequestInfo | URL, init: RequestInit | undefined, consume: (response: Response) => Promise<T>): Promise<T>;
export function managedFetch<T>(input: RequestInfo | URL, init?: RequestInit, consume?: (response: Response) => Promise<T>): Promise<Response | T> {
    const scope = getRequestScope();
    const method = (init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
    const url = input instanceof Request ? input.url : String(input);
    // Response bodies and streams are single-use. Do not share or retain them.
    // For reusable JSON data use http.queryOptions and useQuery instead.
    const key = ['native-request', scope, method, url, ++sequence] as const;
    const operation = async (querySignal?: AbortSignal): Promise<Response | T> => {
        const controller = new AbortController();
        const callerSignal = init?.signal || (input instanceof Request ? input.signal : undefined);
        const abort = () => controller.abort();
        querySignal?.addEventListener('abort', abort, { once: true });
        callerSignal?.addEventListener('abort', abort, { once: true });
        const unsubscribe = subscribeRequestScope(abort);
        if (querySignal?.aborted || callerSignal?.aborted || scope !== getRequestScope()) abort();
        try {
            const response = await globalThis.fetch(input, { ...init, signal: controller.signal });
            // A consumer keeps the query/mutation pending until JSON, a download,
            // or the complete streaming result has been read and validated.
            return consume ? await consume(response) : response;
        } finally {
            unsubscribe();
            querySignal?.removeEventListener('abort', abort);
            // Without a consumer, the caller still owns the unread body.
            if (consume) callerSignal?.removeEventListener('abort', abort);
        }
    };
    if ((method !== 'GET' && method !== 'HEAD') || /\/awin\/click(?:[/?]|$)/.test(url)) {
        return executeMutation(key, () => operation());
    }
    return queryClient.fetchQuery({
        queryKey: key,
        queryFn: ({ signal }) => operation(signal),
        retry: false,
        staleTime: 0,
        gcTime: 0,
        structuralSharing: false,
    });
}
